import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EmbeddedRemoteApplicationComponent, RemoteAppConfig } from '@itero/standard-launch-sdk';
import { takeUntil } from 'rxjs/operators';
import { tap } from 'rxjs';
import { BaseDestroyable } from '@core/base-destroyable';
import { Location } from '@angular/common';
import { STANDARD_REMOTE_URL_PREFIX } from 'app/remote-application/consts';
import { isStandardRemoteApplication } from 'app/remote-application/helpers/is-standard-remote-application';
import { TimberService } from '@logging/timber.service';

@Component({
	selector: 'itero-remote-application',
	standalone: true,
	imports: [EmbeddedRemoteApplicationComponent],
	providers: [],
	styleUrls: ['remote-application-container.component.scss'],
	templateUrl: 'remote-application-container.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteApplicationContainerComponent extends BaseDestroyable implements OnInit, OnDestroy {
	readonly descriptor = signal<RemoteAppConfig | undefined>(undefined);

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly location: Location,
		private readonly timberService: TimberService
	) {
		super();
	}

	ngOnInit(): void {
		this.activatedRoute.url
			.pipe(
				takeUntil(this.componentAlive$),
				tap(url => {
					const appId = url[1]?.path;

					if (!appId) {
						this.timberService.error('[RemoteApplicationContainerComponent] Missing appId', {
							module: 'RemoteApplicationContainerComponent',
						});

						return;
					}
					if (!isStandardRemoteApplication(appId)) {
						this.timberService.error(`[RemoteApplicationContainerComponent] Invalid appId: ${appId}`, {
							module: 'RemoteApplicationContainerComponent',
						});

						return;
					}

					this.openApplication(appId);
				})
			)
			.subscribe({
				error: error => {
					this.timberService.error('[RemoteApplicationContainerComponent] Error occurred', {
						module: 'RemoteApplicationContainerComponent',
						error,
					});
				},
			});
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	public getPassedParameters(history: History): Record<string, unknown> {
		if (!history.state) {
			return {};
		}

		// Remove the automatically added `navigationId` from the state object
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { navigationId, ...passedParameters } = history.state as Record<string, unknown>;

		return passedParameters;
	}

	private openApplication(appId: string): void {
		const path = this.location.path();
		const remoteAppRoute = `/${STANDARD_REMOTE_URL_PREFIX}/${appId}`;
		let basePath: string | undefined;

		if (path.includes(remoteAppRoute)) {
			basePath = path.substring(0, path.indexOf(remoteAppRoute) + remoteAppRoute.length).replace(/^\/+/, '');
		}

		this.descriptor.set({
			appId,
			appParameters: this.getPassedParameters(history),
			basePath,
		});
	}
}
